import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { 
  Plane, 
  Train, 
  Bus, 
  Car, 
  Ship, 
  Calendar, 
  Clock, 
  FileText, 
  Copy, 
  Check, 
  Edit3, 
  Eye, 
  X, 
  Save, 
  Navigation,
  Compass
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import vouchersData from '../../../../../../../data/vouchers/vouchersData.json';

const travelModes = vouchersData?.travelModes || [
  { label: "Flight", value: "flight" },
  { label: "Train", value: "train" },
  { label: "Bus", value: "bus" },
  { label: "Cab / Chauffeur", value: "cab" },
  { label: "Waterways / Ferry", value: "waterways" }
];

const formatDisplayDateLong = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
};

const getModeIcon = (mode, size = 18) => {
  const m = String(mode || '').toLowerCase();
  if (m === 'train' || m.includes('rail')) return <Train size={size} />;
  if (m === 'bus') return <Bus size={size} />;
  if (m === 'cab' || m === 'car' || m === 'transfer') return <Car size={size} />;
  if (m === 'ferry' || m === 'boat' || m === 'waterways') return <Ship size={size} />;
  return <Plane size={size} />;
};

const TravelDetailsModal = ({
  isOpen,
  onClose,
  travel,
  travelIndex,
  onSaveTravel,
  initialEditMode = false
}) => {
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(initialEditMode);
  const [copiedPnr, setCopiedPnr] = useState(false);
  const [saving, setSaving] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(64);

  // Form draft state
  const [formData, setFormData] = useState({
    mode_of_transport: 'flight',
    operator: '',
    vehicle_number: '',
    pnr: '',
    from: '',
    to: '',
    journey_date: '',
    departure_time: '10:00',
    arrival_date: '',
    arrival_time: '12:00',
    seat_class: '',
    instructions: '',
    added_by: 'agent'
  });

  // Dynamically measure portal header and prevent background scrolling
  useEffect(() => {
    if (!isOpen) return;

    const updateHeaderHeight = () => {
      const headerEl = document.querySelector('header');
      if (headerEl) {
        const rect = headerEl.getBoundingClientRect();
        if (rect.height > 0) {
          setHeaderHeight(rect.height);
          document.documentElement.style.setProperty('--portal-header-height', `${rect.height}px`);
        }
      }
    };

    updateHeaderHeight();
    window.addEventListener('resize', updateHeaderHeight);

    const prevBodyOverflow = document.body.style.overflow;
    const mainEl = document.querySelector('main');
    const prevMainOverflow = mainEl ? mainEl.style.overflow : '';

    document.body.style.overflow = 'hidden';
    if (mainEl) mainEl.style.overflow = 'hidden';

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose?.();
      }
    };
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('resize', updateHeaderHeight);
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = prevBodyOverflow;
      if (mainEl) mainEl.style.overflow = prevMainOverflow;
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (travel) {
      setFormData({
        mode_of_transport: travel.mode_of_transport || travel.type || 'flight',
        operator: travel.operator || '',
        vehicle_number: travel.vehicle_number || travel.flight_number || travel.identifier || '',
        pnr: travel.pnr || travel.confirmation_code || '',
        from: travel.from || travel.origin || '',
        to: travel.to || travel.destination || '',
        journey_date: travel.journey_date || '',
        departure_time: travel.departure_time || '10:00',
        arrival_date: travel.arrival_date || travel.journey_date || '',
        arrival_time: travel.arrival_time || '12:00',
        seat_class: travel.seat_class || travel.class || travel.seat_coach || '',
        instructions: travel.instructions || travel.remarks || '',
        added_by: travel.added_by || 'agent'
      });
      setIsEditing(initialEditMode);
    }
  }, [travel, initialEditMode, isOpen]);

  if (!isOpen || !travel) return null;

  const handleCopyPnr = (code) => {
    if (!code) return;
    navigator.clipboard?.writeText(code);
    setCopiedPnr(true);
    toast.success('PNR copied!');
    setTimeout(() => setCopiedPnr(false), 2000);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      const updatedTravel = {
        ...travel,
        ...formData,
        flight_number: formData.vehicle_number,
        identifier: formData.vehicle_number,
        origin: formData.from,
        destination: formData.to
      };

      await onSaveTravel(travelIndex, updatedTravel);
      setIsEditing(false);
      toast.success('Travel details updated successfully!');
    } catch (err) {
      console.error('Failed to update travel:', err);
      toast.error('Failed to update travel details.');
    } finally {
      setSaving(false);
    }
  };

  const handleTrack = () => {
    const mode = (formData.mode_of_transport || '').toLowerCase();
    const identifier = formData.vehicle_number;
    if (!identifier) return;

    if (mode === 'train') {
      navigate(`/tracking/train-tracking?train_number=${identifier}&journey_date=${formData.journey_date || ''}`);
    } else if (mode === 'flight') {
      navigate(`/tracking/flight-tracking?flight_number=${identifier}`);
    } else {
      window.open(`https://www.google.com/search?q=track+${encodeURIComponent(formData.operator || '')}+${encodeURIComponent(identifier)}`, '_blank');
    }
  };

  const travelTitle = formData.operator
    ? `${formData.operator} · ${formData.vehicle_number || 'Travel'}`
    : `${formData.mode_of_transport ? formData.mode_of_transport.toUpperCase() : 'Travel'} ${formData.vehicle_number ? `· ${formData.vehicle_number}` : ''}`;

  const modalContent = (
    <div 
      className="fixed inset-x-0 bottom-0 z-50 flex items-center justify-center p-3 sm:p-5 bg-slate-900/60 backdrop-blur-sm animate-fade-in"
      style={{ top: `${headerHeight}px`, height: `calc(100vh - ${headerHeight}px)` }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div 
        className="bg-white rounded-3xl shadow-2xl border border-slate-200/90 w-full max-w-2xl flex flex-col overflow-hidden animate-scale-in"
        style={{ maxHeight: `calc(100% - 1.5rem)` }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Top Bar */}
        <div className="px-5 sm:px-6 py-3.5 border-b border-slate-100 flex items-center justify-between gap-3 bg-white shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-2xl bg-teal-50 border border-teal-100/80 flex items-center justify-center text-[#067978] shrink-0 shadow-2xs">
              {getModeIcon(formData.mode_of_transport, 20)}
            </div>
            <div className="min-w-0">
              <h3 className="text-base sm:text-lg font-semibold text-slate-800 truncate">
                {travelTitle}
              </h3>
              <p className="text-xs text-slate-500 font-normal truncate">
                {formData.from && formData.to ? `${formData.from} → ${formData.to}` : 'Transit Journey'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {/* View / Edit Mode Toggle Pills */}
            <div className="inline-flex items-center bg-slate-100 p-0.5 rounded-xl border border-slate-200/60">
              <button
                type="button"
                onClick={() => setIsEditing(false)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer ${
                  !isEditing
                    ? 'bg-white text-[#067978] shadow-xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <Eye size={13} />
                <span className="hidden sm:inline">View</span>
              </button>
              <button
                type="button"
                onClick={() => setIsEditing(true)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer ${
                  isEditing
                    ? 'bg-white text-[#067978] shadow-xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <Edit3 size={13} />
                <span className="hidden sm:inline">Edit</span>
              </button>
            </div>

            {/* Close Button */}
            <button
              type="button"
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-xl transition-colors cursor-pointer"
              aria-label="Close modal"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Modal Scrollable Body */}
        <div className="p-5 sm:p-6 overflow-y-auto space-y-4 no-scrollbar flex-1 overscroll-contain">
          {!isEditing ? (
            /* ================= VIEW MODE: REFINED LIGHT TYPOGRAPHY ================= */
            <div className="space-y-4">
              {/* Route Diagram Hero Card */}
              <div className="p-4 sm:p-5 rounded-2xl bg-gradient-to-br from-teal-50/90 via-emerald-50/40 to-slate-50 border border-teal-200/70 shadow-2xs">
                <div className="flex items-center justify-between gap-3 text-center sm:text-left">
                  {/* Origin */}
                  <div className="flex-1 min-w-0">
                    <span className="text-[10px] font-medium uppercase tracking-wide text-teal-700 bg-teal-100/60 px-2 py-0.5 rounded-md">
                      Origin
                    </span>
                    <div className="text-sm sm:text-base font-medium text-slate-800 mt-1 truncate">
                      {formData.from || 'Origin City'}
                    </div>
                    <div className="text-xs text-slate-500 font-mono font-normal mt-0.5 flex items-center justify-center sm:justify-start gap-1">
                      <Clock size={12} className="text-teal-600" />
                      <span>{formData.departure_time || '10:00'} (24-hour)</span>
                    </div>
                  </div>

                  {/* Route Indicator in Middle */}
                  <div className="flex flex-col items-center justify-center px-2 shrink-0">
                    <div className="w-8 h-8 rounded-full bg-white text-[#067978] shadow-2xs border border-teal-100 flex items-center justify-center mb-1">
                      {getModeIcon(formData.mode_of_transport, 15)}
                    </div>
                    <span className="text-[10px] font-medium uppercase tracking-wide text-[#067978]">
                      {formData.mode_of_transport}
                    </span>
                  </div>

                  {/* Destination */}
                  <div className="flex-1 min-w-0 sm:text-right">
                    <span className="text-[10px] font-medium uppercase tracking-wide text-slate-600 bg-slate-200/70 px-2 py-0.5 rounded-md">
                      Destination
                    </span>
                    <div className="text-sm sm:text-base font-medium text-slate-800 mt-1 truncate">
                      {formData.to || 'Destination City'}
                    </div>
                    <div className="text-xs text-slate-500 font-mono font-normal mt-0.5 flex items-center justify-center sm:justify-end gap-1">
                      <Clock size={12} className="text-slate-400" />
                      <span>{formData.arrival_time || '12:00'} (24-hour)</span>
                    </div>
                  </div>
                </div>

                <div className="mt-4 pt-3 border-t border-teal-200/50 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-600">
                  <div className="flex items-center gap-1.5">
                    <Calendar size={13} className="text-[#067978]" />
                    <span className="font-normal text-slate-500">Journey Date:</span>
                    <span className="font-medium text-slate-700">{formatDisplayDateLong(formData.journey_date)}</span>
                  </div>

                  {formData.vehicle_number && (
                    <button
                      type="button"
                      onClick={handleTrack}
                      className="px-3 py-1 bg-[#067978] hover:bg-[#056564] text-white text-xs font-medium rounded-lg shadow-2xs transition-all cursor-pointer flex items-center gap-1"
                    >
                      <Compass size={12} />
                      <span>Track Journey</span>
                    </button>
                  )}
                </div>
              </div>

              {/* Details Grid (Carrier, PNR, Class, Instructions) */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Carrier & Identification */}
                <div className="p-4 rounded-2xl bg-white border border-slate-200/80 shadow-2xs space-y-3">
                  <div className="flex items-center gap-2 pb-2 border-b border-slate-100 text-xs font-medium uppercase tracking-wide text-slate-500">
                    <Navigation size={14} className="text-[#067978]" />
                    <span>Carrier &amp; Vehicle</span>
                  </div>

                  <div>
                    <span className="text-[11px] text-slate-400 font-normal block">Carrier / Operator</span>
                    <span className="text-xs sm:text-sm font-medium text-slate-700 mt-0.5 block">
                      {formData.operator || 'Not specified'}
                    </span>
                  </div>

                  <div>
                    <span className="text-[11px] text-slate-400 font-normal block">Vehicle / Flight Number</span>
                    <span className="text-xs sm:text-sm font-mono font-medium text-slate-700 mt-0.5 block">
                      {formData.vehicle_number || '—'}
                    </span>
                  </div>

                  <div>
                    <span className="text-[11px] text-slate-400 font-normal block">Seat / Class</span>
                    <span className="text-xs font-normal text-slate-600 mt-0.5 block">
                      {formData.seat_class || 'Standard / Economy'}
                    </span>
                  </div>
                </div>

                {/* PNR & Booking Reference Card */}
                <div className="p-4 rounded-2xl bg-white border border-slate-200/80 shadow-2xs space-y-3">
                  <div className="flex items-center gap-2 pb-2 border-b border-slate-100 text-xs font-medium uppercase tracking-wide text-slate-500">
                    <FileText size={14} className="text-[#067978]" />
                    <span>Booking &amp; PNR</span>
                  </div>

                  <div>
                    <span className="text-[11px] text-slate-400 font-normal block">PNR / Reference</span>
                    {formData.pnr ? (
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs sm:text-sm font-mono font-medium text-slate-700 bg-slate-50 border border-slate-200 px-2 py-0.5 rounded-md">
                          {formData.pnr}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleCopyPnr(formData.pnr)}
                          className="p-1 text-slate-400 hover:text-[#067978] transition-colors cursor-pointer"
                          title="Copy PNR"
                        >
                          {copiedPnr ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} />}
                        </button>
                      </div>
                    ) : (
                      <span className="text-xs text-slate-400 italic">No PNR attached</span>
                    )}
                  </div>

                  <div>
                    <span className="text-[11px] text-slate-400 font-normal block">Attribution</span>
                    <span className="text-xs font-normal text-slate-600 capitalize mt-0.5 block">
                      Added by {formData.added_by === 'dmc' ? 'DMC' : 'Agent'}
                    </span>
                  </div>
                </div>

                {/* Instructions / Remarks */}
                {formData.instructions && (
                  <div className="sm:col-span-2 p-4 rounded-2xl bg-amber-50/40 border border-amber-200/60 shadow-2xs space-y-1.5">
                    <span className="text-xs font-medium uppercase tracking-wide text-amber-800 block">
                      Travel Instructions &amp; Notes
                    </span>
                    <p className="text-xs text-slate-700 leading-relaxed font-normal">
                      {formData.instructions}
                    </p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* ================= EDIT MODE: REFINED LIGHT TYPOGRAPHY FORM ================= */
            <form id="travel-edit-form" onSubmit={handleSave} className="space-y-4">
              <div className="bg-slate-50/70 p-4 rounded-2xl border border-slate-200/80 space-y-3.5">
                <span className="text-xs font-medium uppercase tracking-wide text-slate-500 block">
                  Transport Identification
                </span>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-normal text-slate-600 mb-1">
                      Mode of Transport
                    </label>
                    <select
                      value={formData.mode_of_transport}
                      onChange={(e) => handleInputChange('mode_of_transport', e.target.value)}
                      className="w-full text-xs sm:text-sm font-normal bg-white border border-slate-200 rounded-xl px-3 py-2 text-slate-800 focus:outline-none focus:border-[#067978] cursor-pointer"
                    >
                      {travelModes.map((m) => (
                        <option key={m.value} value={m.value}>
                          {m.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-normal text-slate-600 mb-1">
                      Operator / Carrier Name
                    </label>
                    <input
                      type="text"
                      value={formData.operator}
                      onChange={(e) => handleInputChange('operator', e.target.value)}
                      placeholder="e.g. Indian Railways / IndiGo"
                      className="w-full text-xs sm:text-sm font-normal bg-white border border-slate-200 rounded-xl px-3 py-2 text-slate-800 focus:outline-none focus:border-[#067978]"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-normal text-slate-600 mb-1">
                      Vehicle / Flight Number
                    </label>
                    <input
                      type="text"
                      value={formData.vehicle_number}
                      onChange={(e) => handleInputChange('vehicle_number', e.target.value)}
                      placeholder="e.g. 1221212 or 6E 214"
                      className="w-full text-xs sm:text-sm font-mono font-normal bg-white border border-slate-200 rounded-xl px-3 py-2 text-slate-800 focus:outline-none focus:border-[#067978]"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-normal text-slate-600 mb-1">
                      PNR / Booking Reference
                    </label>
                    <input
                      type="text"
                      value={formData.pnr}
                      onChange={(e) => handleInputChange('pnr', e.target.value)}
                      placeholder="e.g. IR-882194"
                      className="w-full text-xs sm:text-sm font-mono font-normal bg-white border border-slate-200 rounded-xl px-3 py-2 text-slate-800 focus:outline-none focus:border-[#067978]"
                    />
                  </div>
                </div>
              </div>

              {/* Route & Timings (24-hour format) */}
              <div className="bg-slate-50/70 p-4 rounded-2xl border border-slate-200/80 space-y-3.5">
                <span className="text-xs font-medium uppercase tracking-wide text-slate-500 block">
                  Route &amp; 24-Hour Timings
                </span>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-normal text-slate-600 mb-1">
                      Origin (From)
                    </label>
                    <input
                      type="text"
                      value={formData.from}
                      onChange={(e) => handleInputChange('from', e.target.value)}
                      placeholder="e.g. Mumbai CSMT"
                      className="w-full text-xs sm:text-sm font-normal bg-white border border-slate-200 rounded-xl px-3 py-2 text-slate-800 focus:outline-none focus:border-[#067978]"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-normal text-slate-600 mb-1">
                      Destination (To)
                    </label>
                    <input
                      type="text"
                      value={formData.to}
                      onChange={(e) => handleInputChange('to', e.target.value)}
                      placeholder="e.g. Goa Madgaon (MAO)"
                      className="w-full text-xs sm:text-sm font-normal bg-white border border-slate-200 rounded-xl px-3 py-2 text-slate-800 focus:outline-none focus:border-[#067978]"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-normal text-slate-600 mb-1">
                      Departure Date
                    </label>
                    <input
                      type="date"
                      value={formData.journey_date}
                      onChange={(e) => handleInputChange('journey_date', e.target.value)}
                      className="w-full text-xs sm:text-sm font-normal bg-white border border-slate-200 rounded-xl px-3 py-2 text-slate-800 focus:outline-none focus:border-[#067978]"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-normal text-slate-600 mb-1">
                      Departure Time (24h)
                    </label>
                    <input
                      type="text"
                      value={formData.departure_time}
                      onChange={(e) => handleInputChange('departure_time', e.target.value)}
                      placeholder="06:40"
                      className="w-full text-xs sm:text-sm font-mono font-normal bg-white border border-slate-200 rounded-xl px-3 py-2 text-slate-800 focus:outline-none focus:border-[#067978]"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-normal text-slate-600 mb-1">
                      Arrival Date
                    </label>
                    <input
                      type="date"
                      value={formData.arrival_date}
                      onChange={(e) => handleInputChange('arrival_date', e.target.value)}
                      className="w-full text-xs sm:text-sm font-normal bg-white border border-slate-200 rounded-xl px-3 py-2 text-slate-800 focus:outline-none focus:border-[#067978]"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-normal text-slate-600 mb-1">
                      Arrival Time (24h)
                    </label>
                    <input
                      type="text"
                      value={formData.arrival_time}
                      onChange={(e) => handleInputChange('arrival_time', e.target.value)}
                      placeholder="14:00"
                      className="w-full text-xs sm:text-sm font-mono font-normal bg-white border border-slate-200 rounded-xl px-3 py-2 text-slate-800 focus:outline-none focus:border-[#067978]"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-normal text-slate-600 mb-1">
                    Seat / Class / Coach
                  </label>
                  <input
                    type="text"
                    value={formData.seat_class}
                    onChange={(e) => handleInputChange('seat_class', e.target.value)}
                    placeholder="e.g. Economy / 3A Coach B2"
                    className="w-full text-xs sm:text-sm font-normal bg-white border border-slate-200 rounded-xl px-3 py-2 text-slate-800 focus:outline-none focus:border-[#067978]"
                  />
                </div>

                <div>
                  <label className="block text-xs font-normal text-slate-600 mb-1">
                    Special Instructions / Notes
                  </label>
                  <textarea
                    rows={2}
                    value={formData.instructions}
                    onChange={(e) => handleInputChange('instructions', e.target.value)}
                    placeholder="e.g. Please carry government photo ID for check-in"
                    className="w-full text-xs sm:text-sm font-normal bg-white border border-slate-200 rounded-xl px-3 py-2 text-slate-800 focus:outline-none focus:border-[#067978]"
                  />
                </div>
              </div>
            </form>
          )}
        </div>

        {/* Modal Pinned Actions Footer */}
        <div className="px-5 sm:px-6 py-3.5 border-t border-slate-100 flex items-center justify-end gap-3 bg-white shrink-0">
          {!isEditing ? (
            <div className="flex items-center gap-2.5">
              {formData.vehicle_number && (
                <button
                  type="button"
                  onClick={handleTrack}
                  className="px-4 py-2 bg-teal-50 hover:bg-teal-100/80 text-[#067978] border border-teal-200/80 text-xs sm:text-sm font-medium rounded-xl transition-all shadow-xs flex items-center gap-1.5 cursor-pointer active:scale-95"
                >
                  <Compass size={14} />
                  <span>Track Journey</span>
                </button>
              )}
              <button
                type="button"
                onClick={() => setIsEditing(true)}
                className="px-4 py-2 bg-[#067978] hover:bg-[#056564] text-white text-xs sm:text-sm font-medium rounded-xl transition-all shadow-xs flex items-center gap-1.5 cursor-pointer active:scale-95"
              >
                <Edit3 size={14} />
                <span>Edit Travel Details</span>
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setIsEditing(false)}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs sm:text-sm font-medium rounded-xl transition-all cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="submit"
                form="travel-edit-form"
                disabled={saving}
                className="px-5 py-2 bg-[#067978] hover:bg-[#056564] text-white text-xs sm:text-sm font-medium rounded-xl shadow-xs transition-all cursor-pointer active:scale-95 disabled:opacity-50 flex items-center gap-1.5"
              >
                <Save size={14} />
                <span>{saving ? 'Saving...' : 'Save Changes'}</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(modalContent, document.body) : modalContent;
};

export default TravelDetailsModal;
